import * as z from "zod/v4";
import { participantSchema, utteranceEventMetadataSchema } from "../types";
import type { ReactiveMeetingState, Utterance } from "../meeting";
import { invariant } from "@/lib/invariant";

const voiceAuthenticationChunkSchema = z.object({
  chunk_id: z.string(),
  start_time: z.number(),
  end_time: z.number(),
  identity: z.object({
    result: z.string(),
    confidence: z.string(),
    same_person_prob: z.string(),
    different_person_prob: z.string(),
  }),
  authenticity: z.object({
    result: z.enum(["Fake Voice", "Real Voice"]),
    confidence: z.number().min(0).max(1),
    real_prob: z.number().min(0).max(1),
    fake_prob: z.number().min(0).max(1),
  }),
});
export const voiceAuthenticationSuccessSchema = z.object({
  result_type: z.literal("success"),
  result_message: z.string(),

  status: z.literal("success"),
  analysis_parameters: z.object({
    chunk_size_seconds: z.number().int(),
    overlap_seconds: z.number().int(),
    reference_exists: z.boolean(),
  }),
  overall_score: z.object({
    identity: z.unknown().nullable(),
    authenticity: z.object({
      real_ratio: z.number().min(0).max(1),
      fake_ratio: z.number().min(0).max(1),
    }),
  }),
  results: z.array(voiceAuthenticationChunkSchema),
});
const voiceAuthenticationSkippedSchema = z.object({
  result_type: z.enum(["skipped", "failed"]),
  result_message: z.string(),
});
export const voiceAuthenticationResultSchema = z.discriminatedUnion(
  "result_type",
  [voiceAuthenticationSuccessSchema, voiceAuthenticationSkippedSchema],
);
export type VexonResult = z.infer<typeof voiceAuthenticationResultSchema>;

export const vexonEventDataSchema = z.object({
  transcription_text: z.string(),
  participant: participantSchema,
  error_message: z.string().nullable(),
  ai_results: z
    .object({
      voice_authentication: voiceAuthenticationResultSchema,
    })
    .partial(),
});
export const vexonEventSchema = z.object({
  event_type: z.literal("bot.utterance.vexon_processed"),
  metadata: utteranceEventMetadataSchema,
  data: vexonEventDataSchema,
});

export type VexonEvent = z.infer<typeof vexonEventSchema>;

export function vexonEventReducer(
  ctx: ReactiveMeetingState,
  event: VexonEvent,
): ReactiveMeetingState {
  const utterance = ctx._utterances.get(event.metadata.bot_utterance_id);
  invariant(
    !!utterance,
    "Utterance should have been created before calling this reducer",
  );

  if (event.data.error_message !== null) {
    utterance.vexon = {
      status: "error",
      data: null,
      error: event.data.error_message,
    } as const;
  } else {
    const { ai_results } = event.data;
    utterance.vexon = extractVexonResult(ai_results.voice_authentication);
  }

  ctx._utterances.set(event.metadata.bot_utterance_id, utterance);

  return ctx;
}

export function extractVexonResult(
  vexonResult: VexonResult | undefined,
): Utterance["vexon"] {
  if (!vexonResult) {
    return {
      status: "null",
      data: null,
      error: null,
    };
  } else
    switch (vexonResult.result_type) {
      case "success":
        return {
          status: "success",
          data: {
            fakeRatio: vexonResult.overall_score.authenticity.fake_ratio,
            chunks: vexonResult.results.map((chunk) => ({
              startTime: chunk.start_time,
              endTime: chunk.end_time,
              isFake: chunk.authenticity.result === "Fake Voice",
            })),
          },
          error: null,
        };
      case "skipped":
        return {
          status: "skipped",
          data: null,
          error: null,
        };
      case "failed":
        return {
          status: "error",
          error: vexonResult.result_message,
          data: null,
        };
      default:
        return {
          status: "error",
          error: "unrecognized pii result status",
          data: null,
        };
    }
}
